import { Dialog, DialogContent } from '@mui/material'
import { Button } from '../ui-primitives/button'
import React from 'react'
import { useEventLogs } from './event-log-store'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../ui-primitives/resizable'
import { Cross2Icon } from '@radix-ui/react-icons'
import { Badge } from '../ui-primitives/badge'
import { mergeClasses } from '../../helpers/styling'
import { Label } from '../ui-primitives/label'
import MultipleSelector from '../ui-primitives/multiple-selector'
import { getNamespaces, GLOBAL_NAMESPACE, usePubsub } from '../../redux/pubsub'

export function EventLogsPopup(props: {
  data?: undefined
  open: boolean
  onClose: () => void
}) {
  const [selectedIdx, setSelectedIdx] = React.useState<number>(0)
  const eventLogs = useEventLogs()
  const [includedNamespaces, setIncludedNamespaces] = React.useState<{value: string, label: string}[]>([])

  const [variables] = usePubsub()
  const namespaces = getNamespaces(variables)

  const filteredLogs = React.useMemo(() => eventLogs.filter(event => {
    switch (event.type) {
      case 'app-variable-update-event': {
        if (event.namespace === GLOBAL_NAMESPACE) return true
        return includedNamespaces.some(n => n.label === event.namespace)
      }
      case 'sparql-request-event': return true
      case 'rule-execution-event': return true
      default: {
        return true
      }
    }
  }), [eventLogs, includedNamespaces])

  if (!eventLogs.length) return (
    <DialogWrapper isOpen={props.open} close={props.onClose}>
      <div className="text-center px-4 py-2 opacity-75">
        No wistor-events have happened, since activating edit-mode.
      </div>
    </DialogWrapper>
  )

  const selected = eventLogs[selectedIdx]
  if (!selected) {
    throw new Error('selected event-log-item not found')
  }

  return (
    <DialogWrapper isOpen={props.open} close={props.onClose}>
      <ResizablePanelGroup direction="horizontal">

        <ResizablePanel className="overflow-auto h-full flex flex-col px-4 py-2" style={{
          height: "calc(100vh - 200px)"
        }}>
          <Label>
            namespaces to include
            <MultipleSelector
              value={includedNamespaces}
              onChange={setIncludedNamespaces}
              options={namespaces.map(n => ({label: n, value: n}))}
            />
          </Label>
          {filteredLogs.map((item, i) => {
            switch (item.type) {

              case 'app-variable-update-event': return (
                <button
                  key={i} // this is fine until we allow this list to be sorted or filtered
                  className={mergeClasses(
                    "bg-yellow-100 flex flex-row text-left space-x-1 items-center font-mono text-sm border-y transition duration-300 ease-in-out hover:scale-105",
                    { "bg-opacity-30": i !== selectedIdx, "scale-105": i === selectedIdx }
                  )}
                  onClick={() => setSelectedIdx(i)}
                >
                  <Badge className="bg-yellow-600">VAR</Badge>
                  {item.namespace !== GLOBAL_NAMESPACE && <Badge className="to-purple-950">{item.namespace}</Badge>}
                  <span className="grow overflow-hidden text-ellipsis whitespace-nowrap">
                    {item.key} := {item.valueNew}
                  </span>
                  <span className="font-mono opacity-75 text-xs">[{item.timestamp}]</span>
                </button>
              )

              case 'sparql-request-event': return (
                <button
                  key={i} // this is fine until we allow this list to be sorted or filtered
                  className={mergeClasses(
                    "bg-green-100 flex flex-row text-left space-x-1 items-center font-mono text-sm border-y transition duration-100 ease-out hover:scale-105",
                    { "bg-opacity-30": i !== selectedIdx, "scale-105": i === selectedIdx }
                  )}
                  onClick={() => setSelectedIdx(i)}
                >
                  <Badge className="bg-green-600">SPARQL</Badge>
                  <span className="grow overflow-hidden text-ellipsis whitespace-nowrap">
                    {item.templateId}
                  </span>
                  <span className="font-mono opacity-75 text-xs">[{item.timestamp}]</span>
                </button>
              )

              case 'rule-execution-event': return (
                <button
                  key={i} // this is fine until we allow this list to be sorted or filtered
                  className={mergeClasses(
                    "bg-purple-100 flex flex-row text-left space-x-1 items-center font-mono text-sm border-y transition duration-300 ease-in-out hover:scale-105",
                    { "bg-opacity-30": i !== selectedIdx, "scale-105": i === selectedIdx }
                  )}
                  onClick={() => setSelectedIdx(i)}
                >
                  <Badge className="bg-purple-600">RULE</Badge>
                  <span className="grow overflow-hidden text-ellipsis whitespace-nowrap">
                    {item.tag}
                  </span>
                  <span className="font-mono opacity-75 text-xs">[{item.timestamp}]</span>
                </button>
              )
              default: throw new Error(`unknown event: ${JSON.stringify(item)}`)
            }
        })}
        </ResizablePanel>

        <ResizableHandle withHandle className="mx-1" />

        <ResizablePanel className="overflow-auto h-full flex flex-col px-4 py-2 text-sm" style={{
          height: "calc(100vh - 200px)"
        }}>{(() => {
          switch (selected.type) {

            case 'app-variable-update-event': return (<>
              <div className="mb-3 flex flex-row justify-between">
                <div>
                  <span className="opacity-75 mr-2">type</span>
                  <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">app-variable update</code>
                </div>
                <div>
                  <span className="opacity-75 mr-2">timestamp</span>
                  <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{selected.timestamp}</code>
                </div>
              </div>
              <div className="mb-3">
                <span className="flex opacity-75">variable identifier</span>
                <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono">{selected.key}</pre>
              </div>
              <div className="mb-3">
                <span className="flex opacity-75">old value</span>
                <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono">{selected.valueOld}</pre>
              </div>
              <div className="mb-3">
                <span className="flex opacity-75">new value</span>
                <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono">{selected.valueNew}</pre>
              </div>
            </>)

            case 'sparql-request-event': return (<>
              <div className="mb-3 flex flex-row justify-between">
                <div>
                  <span className="opacity-75 mr-2">type</span>
                  <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">sparql request</code>
                </div>
                <div>
                  <span className="opacity-75 mr-2">timestamp</span>
                  <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{selected.timestamp}</code>
                </div>
              </div>
              <div className="mb-3">
                <span className="flex opacity-75">request</span>
                <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono">{selected.request}</pre>
              </div>
              <div className="mb-3">
                <span className="flex opacity-75">response</span>
                <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono whitespace-pre-wrap">{selected.response}</pre>
              </div>
            </>)

            case 'rule-execution-event': return (<>
              <div className="mb-3 flex flex-row justify-between">
                <div>
                  <span className="opacity-75 mr-2">type</span>
                  <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">rule execution</code>
                </div>
                <div>
                  <span className="opacity-75 mr-2">timestamp</span>
                  <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{selected.timestamp}</code>
                </div>
              </div>
              <div className="mb-3">
                <span className="flex opacity-75">tag</span>
                <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono">{selected.tag}</pre>
              </div>
              <div className="mb-3">
                <span className="flex opacity-75">parameters</span>
                <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono">{selected.params}</pre>
              </div>
            </>)

            default: throw new Error(`unknown event: ${JSON.stringify(selected)}`)
          }
        })()}</ResizablePanel>
      </ResizablePanelGroup>
    </DialogWrapper>
  )
}

function DialogWrapper(props: {
  children: React.ReactNode,
  isOpen: boolean,
  close: () => void,
}): React.ReactNode {
  return (
    <Dialog open={props.isOpen} fullWidth maxWidth="xl" onClose={props.close}>
      <DialogContent className="p-0" style={{
        fontFamily: "'Exo 2 Variable', sans-serif",
      }}>
        <div className="space-y-1.5 text-center sm:text-left bg-wistor text-wistor-foreground py-1">
          Event Logs
        </div>
        <Button variant="ghost" className="text-wistor-foreground absolute right-0 top-0 p-0 h-8 w-8"
          onClick={props.close}
        >
          <Cross2Icon className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </Button>
        {props.children}
      </DialogContent>
    </Dialog>
  )
}
